import React, { useState } from 'react';
import { Building2, Search, Filter, Database, GitMerge, CheckCircle, Loader2, AlertCircle, Award, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  extractDegreePrefix,
  calculateScore,
  cleanQueryForKeywords,
  extractWordsWithMetadata,
  SEARCH_CONFIG,
  getPrefixVariant
} from '../utils/rncpSearchUtils';

const OnboardingTest = () => {
  // États
  const [siret, setSiret] = useState('');
  const [step, setStep] = useState(0); // 0: initial, 1: formations, 2: filtered, 3: rncp, 4: enriched, 5: pairs

  // Données
  const [rawFormations, setRawFormations] = useState([]);
  const [uniqueFormations, setUniqueFormations] = useState([]);
  const [rncpFormations, setRncpFormations] = useState([]);
  const [enrichedFormations, setEnrichedFormations] = useState([]);
  const [pairs, setPairs] = useState([]);

  // Organisme
  const [organism, setOrganism] = useState(null);

  // États de chargement
  const [loading, setLoading] = useState(false);
  const [currentProgress, setCurrentProgress] = useState({ current: 0, total: 0, message: '' });

  // Formatage du SIRET
  const formatSiret = (value) => {
    const numbers = value.replace(/\D/g, '').substring(0, 14);
    const parts = [];

    if (numbers.length > 0) parts.push(numbers.substring(0, 3));
    if (numbers.length > 3) parts.push(numbers.substring(3, 6));
    if (numbers.length > 6) parts.push(numbers.substring(6, 9));
    if (numbers.length > 9) parts.push(numbers.substring(9, 11));
    if (numbers.length > 11) parts.push(numbers.substring(11, 14));

    return parts.join(' ');
  };

  // FONCTION COPIÉE DE INTERCARIFOREF-TEST
  // Fonction de scoring côté client
  const calculateClientScore = (query, title, rncpCode = '', isActive = true) => {
    return calculateScore(query, title, rncpCode, isActive);
  };

  // ÉTAPE 1: Récupérer les formations depuis le SIRET (COPIÉ DE INTERCARIFOREF-TEST)
  const handleFetchFormations = async () => {
    const cleanSiret = siret.replace(/\s/g, '');

    if (!cleanSiret) {
      toast.error('Veuillez entrer un SIRET');
      return;
    }

    if (cleanSiret.length !== 14 || !/^\d{14}$/.test(cleanSiret)) {
      toast.error('Le SIRET doit contenir exactement 14 chiffres');
      return;
    }

    setLoading(true);
    setCurrentProgress({ current: 0, total: 1, message: 'Recherche de l\'organisme...' });

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/intercariforef/scrape`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ siret: cleanSiret })
        }
      );

      const data = await response.json();

      if (data.success && data.data) {
        setOrganism({
          name: data.data.organism?.name || 'Organisme trouvé',
          url: data.data.organismUrl,
          siret: data.data.siret,
          address: data.data.organism?.address,
          totalFormations: data.data.totalFormations
        });

        if (data.data.formations) {
          const formations = data.data.formations.map((f, idx) => ({
            id: idx,
            title: f.title,
            location: f.location,
            url: f.url
          }));

          setRawFormations(formations);
          setStep(1);
          toast.success(`${formations.length} formations récupérées !`);
          console.log('📋 [TEST] Formations récupérées:', formations);
        }
      } else {
        toast.error(data.error || 'Organisme non trouvé pour ce SIRET');
      }
    } catch (err) {
      console.error('❌ Erreur:', err);
      toast.error('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
      setCurrentProgress({ current: 0, total: 0, message: '' });
    }
  };

  // ÉTAPE 2: Filtrer les formations pour garder les uniques (COPIÉ DE INTERCARIFOREF-TEST)
  const handleFilterUnique = () => {
    setLoading(true);
    setCurrentProgress({ current: 0, total: 1, message: 'Filtrage des doublons...' });

    // Dédupliquer les formations par titre
    const formationsMap = new Map();

    rawFormations.forEach(formation => {
      const normalizedTitle = formation.title.trim().toLowerCase();

      if (formationsMap.has(normalizedTitle)) {
        // Formation déjà présente, ajouter le lieu à la liste
        const existing = formationsMap.get(normalizedTitle);
        if (formation.location && !existing.locations.includes(formation.location)) {
          existing.locations.push(formation.location);
        }
      } else {
        // Nouvelle formation, créer l'entrée
        formationsMap.set(normalizedTitle, {
          ...formation,
          locations: formation.location ? [formation.location] : [],
          originalLocation: formation.location
        });
      }
    });

    // Convertir en tableau de formations uniques
    const filtered = Array.from(formationsMap.values());
    setUniqueFormations(filtered);
    setStep(2);

    toast.success(`${filtered.length} formations uniques (${rawFormations.length - filtered.length} doublons supprimés)`);
    console.log('✨ [TEST] Formations uniques:', filtered);

    setLoading(false);
    setCurrentProgress({ current: 0, total: 0, message: '' });
  };

  // FONCTION COPIÉE DE INTERCARIFOREF-TEST
  // Enrichir une formation avec le RNCP
  const enrichFormationWithRncp = async (formation, index) => {
    try {
      // Délai de 5 secondes entre chaque formation (augmenté pour éviter rate limiting)
      if (index > 0) {
        await new Promise(resolve => setTimeout(resolve, 5000));
      }

      const apiSearchTerm = formation.title;
      console.log(`\n🔍 Recherche RNCP pour: "${apiSearchTerm}"`);

      // Extraire le préfixe de diplôme
      const { prefix, coreQuery } = extractDegreePrefix(apiSearchTerm);

      if (prefix) {
        console.log(`  📌 Préfixe détecté: "${prefix}"`);
        console.log(`  📌 Requête core: "${coreQuery}"`);
      }

      const baseUrl = `${import.meta.env.VITE_API_URL}/rncp/smart-search`;
      const headers = {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      };

      // RECHERCHE 1 : Titre original complet
      console.log(`  🔍 [TYPE 1] Recherche avec titre complet: "${apiSearchTerm}"`);
      const searchPromises = [
        fetch(`${baseUrl}?q=${encodeURIComponent(apiSearchTerm)}&limit=100`, { headers })
          .then(r => r.json())
          .then(data => ({ ...data, searchType: 1 }))
          .catch(() => ({ success: false, data: [], searchType: 1 }))
      ];

      // RECHERCHE 2 : Sans le préfixe complet
      if (prefix && coreQuery && coreQuery !== apiSearchTerm) {
        console.log(`  🔍 [TYPE 2] Recherche sans préfixe "${prefix}": "${coreQuery}"`);
        searchPromises.push(
          fetch(`${baseUrl}?q=${encodeURIComponent(coreQuery)}&limit=100`, { headers })
            .then(r => r.json())
            .then(data => ({ ...data, searchType: 2 }))
            .catch(() => ({ success: false, data: [], searchType: 2 }))
        );
      }

      // RECHERCHE 3 : Mots-clés spécifiques
      const { stopWords, genericWords } = SEARCH_CONFIG;

      const queryForKeywords = (prefix ? coreQuery : apiSearchTerm)
        .replace(/-/g, ' ')
        .replace(/[,;:.!?()]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

      const words = queryForKeywords.split(' ')
        .filter(w => w.length > 3 && !stopWords.includes(w.toLowerCase()))
        .map((w, index) => ({
          word: w,
          isGeneric: genericWords.includes(w.toLowerCase()),
          length: w.length,
          originalIndex: index
        }));

      const specificWords = words.filter(w => !w.isGeneric);

      // TYPE 5 : 2 premiers mots
      if (specificWords.length >= 2) {
        const keyWords2 = specificWords.slice(0, 2).map(w => w.word).join(' ');
        if (keyWords2 && keyWords2 !== apiSearchTerm && keyWords2 !== coreQuery) {
          console.log(`  🔍 [TYPE 5] Recherche mots-clés spécifiques (2 mots): "${keyWords2}"`);
          searchPromises.push(
            fetch(`${baseUrl}?q=${encodeURIComponent(keyWords2)}&limit=100`, { headers })
              .then(r => r.json())
              .then(data => ({ ...data, searchType: 5 }))
              .catch(() => ({ success: false, data: [], searchType: 5 }))
          );
        }
      }

      // TYPE 6 : Préfixe + 1 mot spécifique
      if (prefix && specificWords.length >= 1) {
        const firstWord = specificWords[0].word;
        const queryWithPrefix = `${prefix} ${firstWord}`;
        console.log(`  🔍 [TYPE 6] Recherche préfixe + 1er mot: "${queryWithPrefix}"`);
        searchPromises.push(
          fetch(`${baseUrl}?q=${encodeURIComponent(queryWithPrefix)}&limit=100`, { headers })
            .then(r => r.json())
            .then(data => ({ ...data, searchType: 6 }))
            .catch(() => ({ success: false, data: [], searchType: 6 }))
        );
      }

      // TYPE 7 : Variante de préfixe
      if (prefix) {
        const prefixVariant = getPrefixVariant(prefix);
        if (prefixVariant && prefixVariant !== prefix.toUpperCase()) {
          const queryWithVariant = `${prefixVariant} ${coreQuery}`;
          console.log(`  🔍 [TYPE 7] Recherche avec variante de préfixe: "${prefix}" → "${prefixVariant}" : "${queryWithVariant}"`);
          searchPromises.push(
            fetch(`${baseUrl}?q=${encodeURIComponent(queryWithVariant)}&limit=100`, { headers })
              .then(r => r.json())
              .then(data => ({ ...data, searchType: 7 }))
              .catch(() => ({ success: false, data: [], searchType: 7 }))
          );
        }
      }

      // Exécuter les recherches en parallèle
      const responses = await Promise.all(searchPromises);

      // Combiner et dédupliquer les résultats
      let allResults = [];
      const seenRncps = new Map();

      responses.forEach(response => {
        if (response.success && response.data && response.data.certifications) {
          const certs = response.data.certifications;
          certs.forEach(cert => {
            const rncpCode = cert.rncpCode || cert.code_rncp;
            if (rncpCode && !seenRncps.has(rncpCode)) {
              seenRncps.set(rncpCode, response.searchType);
              allResults.push({
                ...cert,
                searchType: response.searchType
              });
            }
          });
        }
      });

      console.log(`  📊 Total résultats combinés: ${allResults.length}`);

      // Créer un objet data compatible
      const data = {
        success: allResults.length > 0,
        data: allResults
      };

      if (data.success && data.data && data.data.length > 0) {
        // Appliquer le scoring
        const scoredResults = data.data.map(cert => {
          const title = cert.intitule || cert.intitule_diplome || 'Sans titre';
          const rncpCode = cert.rncpCode || cert.code_rncp || '';

          // Détecter si le RNCP est inactif
          const isInactive = cert.isActive === false;

          const score = calculateClientScore(formation.title, title, rncpCode, !isInactive);

          return {
            ...cert,
            relevanceScore: score,
            isActive: !isInactive,
            searchType: cert.searchType
          };
        })
        .filter(cert => cert.relevanceScore >= 35)
        .sort((a, b) => {
          if (b.relevanceScore !== a.relevanceScore) {
            return b.relevanceScore - a.relevanceScore;
          }
          const titleA = a.intitule || a.intitule_diplome || '';
          const titleB = b.intitule || b.intitule_diplome || '';
          return titleA.length - titleB.length;
        });

        if (scoredResults.length > 0) {
          const bestMatch = scoredResults[0];
          const score = bestMatch.relevanceScore;

          const searchTypeLabel = bestMatch.searchType ? `[TYPE ${bestMatch.searchType}]` : '';
          console.log(`🎯 ${searchTypeLabel} Meilleur match: "${bestMatch.intitule || bestMatch.intitule_diplome}" (score: ${Math.round(score)})`);

          return {
            ...formation,
            rncp_code: bestMatch.rncpCode || bestMatch.code_rncp,
            rncp_title: bestMatch.intitule || bestMatch.intitule_diplome,
            rncp_match_score: score,
            has_rncp: true,
            rncpData: bestMatch
          };
        }
      }

      // Aucun résultat trouvé
      return {
        ...formation,
        has_rncp: false
      };
    } catch (error) {
      console.error(`Erreur enrichissement "${formation.title}":`, error);
      return {
        ...formation,
        has_rncp: false
      };
    }
  };

  // ÉTAPE 3: Rechercher les codes RNCP (un par un avec délai)
  const handleSearchRNCP = async () => {
    setLoading(true);
    const results = [];

    for (let i = 0; i < uniqueFormations.length; i++) {
      const formation = uniqueFormations[i];

      setCurrentProgress({
        current: i + 1,
        total: uniqueFormations.length,
        message: `Recherche RNCP pour "${formation.title.substring(0, 50)}..."`
      });

      const enriched = await enrichFormationWithRncp(formation, i);
      results.push(enriched);
    }

    setRncpFormations(results);
    setStep(3);

    const withRncp = results.filter(f => f.has_rncp).length;
    toast.success(`${withRncp}/${results.length} formations avec code RNCP trouvé`);
    console.log('🎯 [TEST] Formations avec RNCP:', results);

    setLoading(false);
    setCurrentProgress({ current: 0, total: 0, message: '' });
  };

  // ÉTAPE 4: Enrichir avec blocs de compétences
  const handleEnrichCompetences = async () => {
    setLoading(true);
    const formationsWithRncp = rncpFormations.filter(f => f.has_rncp);
    const results = [];

    for (let i = 0; i < formationsWithRncp.length; i++) {
      const formation = formationsWithRncp[i];

      setCurrentProgress({
        current: i + 1,
        total: formationsWithRncp.length,
        message: `Enrichissement: ${formation.rncp_code}`
      });

      try {
        // Nettoyer le code RNCP
        const cleanRncpCode = formation.rncp_code.replace(/^RNCP/i, '');

        console.log(`🔄 [SYNC] Synchronisation de RNCP${cleanRncpCode}...`);

        // ÉTAPE 1: Synchroniser d'abord la certification (récupère blocs + compétences depuis l'API)
        const syncResponse = await fetch(`${import.meta.env.VITE_API_URL}/rncp/sync`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            rncpCodes: [cleanRncpCode]
          })
        });

        const syncData = await syncResponse.json();

        if (syncResponse.ok && syncData.success) {
          console.log(`✅ [SYNC] RNCP${cleanRncpCode} synchronisé:`, syncData.data);

          // Délai de 2 secondes pour laisser la base de données se mettre à jour
          await new Promise(resolve => setTimeout(resolve, 2000));

          // ÉTAPE 2: Récupérer les détails depuis la base de données
          const detailsUrl = `${import.meta.env.VITE_API_URL}/rncp/${cleanRncpCode}/details`;

          console.log(`📥 [DETAILS] Récupération des détails de RNCP${cleanRncpCode}...`);

          const response = await fetch(detailsUrl, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });

          if (response.ok) {
            const data = await response.json();

            if (data.success && data.competences) {
              const competences = data.competences.map(c => c.intitule || c.title || c.description);

              results.push({
                ...formation,
                competences: competences,
                blocs_competences: data.blocks || [],
                nb_competences: competences.length,
                nb_blocs: data.blocks?.length || 0,
                is_enriched: true
              });

              console.log(`✅ [DETAILS] ${competences.length} compétences dans ${data.blocks?.length || 0} blocs pour ${formation.rncp_code}`);
            } else {
              console.warn(`⚠️ [DETAILS] Pas de compétences trouvées pour ${formation.rncp_code}`);
              results.push({
                ...formation,
                competences: [],
                blocs_competences: [],
                is_enriched: false,
                error: 'Aucune compétence trouvée après synchronisation'
              });
            }
          } else {
            console.error(`❌ [DETAILS] Erreur ${response.status} pour ${formation.rncp_code}`);
            results.push({
              ...formation,
              competences: [],
              blocs_competences: [],
              is_enriched: false,
              error: `Erreur HTTP ${response.status}`
            });
          }
        } else {
          console.error(`❌ [SYNC] Échec de synchronisation pour ${formation.rncp_code}:`, syncData.message);
          results.push({
            ...formation,
            competences: [],
            blocs_competences: [],
            is_enriched: false,
            error: syncData.message || 'Échec de synchronisation'
          });
        }
      } catch (error) {
        console.error(`❌ Erreur enrichissement ${formation.rncp_code}:`, error);
        results.push({
          ...formation,
          competences: [],
          blocs_competences: [],
          is_enriched: false,
          error: error.message
        });
      }

      // Délai de 3 secondes entre chaque certification (pour éviter rate limiting)
      if (i < formationsWithRncp.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 3000));
      }
    }

    setEnrichedFormations(results);
    setStep(4);

    const enriched = results.filter(f => f.is_enriched).length;
    const withErrors = results.filter(f => f.error).length;

    if (enriched > 0) {
      toast.success(`${enriched}/${results.length} formations enrichies avec compétences`);
    }
    if (withErrors > 0) {
      toast.error(`${withErrors} formation(s) avec erreur`);
    }

    console.log('📚 [TEST] Formations enrichies:', results);

    setLoading(false);
    setCurrentProgress({ current: 0, total: 0, message: '' });
  };

  // ÉTAPE 5: Trouver les paires de formations
  const handleFindPairs = () => {
    setLoading(true);
    setCurrentProgress({ current: 0, total: 1, message: 'Recherche de paires...' });

    const detectedPairs = [];
    const formationsWithComp = enrichedFormations.filter(f => f.competences && f.competences.length > 0);

    console.log(`🔍 [TEST] Analyse de ${formationsWithComp.length} formations enrichies...`);

    // Fonction pour normaliser les codes RNCP
    const normalizeRncpCode = (code) => {
      if (!code) return '';
      return code.toString().replace(/^RNCP/i, '').trim().toLowerCase();
    };

    // Fonction pour trouver les compétences communes
    const findCommonCompetences = (form1, form2) => {
      const common = [];
      const comp1 = form1.competences || [];
      const comp2 = form2.competences || [];

      comp1.forEach((c1, idx1) => {
        comp2.forEach((c2, idx2) => {
          const similarity = calculateSimilarity(c1, c2);
          if (similarity >= 0.8) {
            common.push({
              competence1: c1,
              competence2: c2,
              similarity: similarity
            });
          }
        });
      });

      return common;
    };

    // Fonction de calcul de similarité
    const calculateSimilarity = (str1, str2) => {
      const normalize = (str) => {
        return str.toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .trim();
      };

      const s1 = normalize(str1);
      const s2 = normalize(str2);

      if (s1 === s2) return 1.0;

      const words1 = new Set(s1.split(/\s+/));
      const words2 = new Set(s2.split(/\s+/));

      const intersection = new Set([...words1].filter(x => words2.has(x)));
      const union = new Set([...words1, ...words2]);

      return intersection.size / union.size;
    };

    // Comparer toutes les formations entre elles
    for (let i = 0; i < formationsWithComp.length; i++) {
      for (let j = i + 1; j < formationsWithComp.length; j++) {
        const form1 = formationsWithComp[i];
        const form2 = formationsWithComp[j];

        // Exclure si même RNCP
        const rncp1 = normalizeRncpCode(form1.rncp_code);
        const rncp2 = normalizeRncpCode(form2.rncp_code);

        if (rncp1 && rncp2 && rncp1 === rncp2) {
          console.log(`🚫 [TEST] Même RNCP, exclusion: ${form1.rncp_code} = ${form2.rncp_code}`);
          continue;
        }

        // Trouver les compétences communes
        const commonCompetences = findCommonCompetences(form1, form2);
        const matchPercentage = (commonCompetences.length / Math.max(form1.competences.length, form2.competences.length)) * 100;

        console.log(`📊 [TEST] Comparaison:`, {
          formation1: form1.title,
          formation2: form2.title,
          rncp1: form1.rncp_code,
          rncp2: form2.rncp_code,
          comp1: form1.competences.length,
          comp2: form2.competences.length,
          common: commonCompetences.length,
          matchPercentage: Math.round(matchPercentage)
        });

        // Si au moins 50% de correspondance
        if (matchPercentage >= 50) {
          detectedPairs.push({
            formation1: form1,
            formation2: form2,
            commonCompetences: commonCompetences,
            matchPercentage: Math.round(matchPercentage)
          });

          console.log(`🎯 [TEST] Paire détectée! ${Math.round(matchPercentage)}% de correspondance`);
        }
      }
    }

    setPairs(detectedPairs);
    setStep(5);

    if (detectedPairs.length > 0) {
      toast.success(`${detectedPairs.length} paire(s) de formations détectée(s) !`);
    } else {
      toast.info('Aucune paire détectée avec au moins 50% de correspondance');
    }

    console.log('🎉 [TEST] Paires détectées:', detectedPairs);

    setLoading(false);
    setCurrentProgress({ current: 0, total: 0, message: '' });
  };

  // Rendu des boutons d'étapes
  const renderStepButtons = () => {
    return (
      <div className="space-y-4">
        {/* Étape 1 */}
        <div className="border-2 border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              step >= 1 ? 'bg-green-500' : 'bg-gray-300'
            }`}>
              {step >= 1 ? <CheckCircle className="w-5 h-5 text-white" /> : <span className="text-white font-bold">1</span>}
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900">Récupérer les formations</h3>
              <p className="text-sm text-gray-600">Depuis Intercariforef (SIRET)</p>
            </div>
            {step >= 1 && (
              <span className="text-sm font-medium text-green-600">{rawFormations.length} formations</span>
            )}
          </div>

          {step === 0 && (
            <div className="space-y-3">
              <input
                type="text"
                value={siret}
                onChange={(e) => setSiret(formatSiret(e.target.value))}
                placeholder="000 000 000 00 000"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                maxLength={18}
              />
              <button
                onClick={handleFetchFormations}
                disabled={loading || siret.replace(/\s/g, '').length !== 14}
                className="w-full bg-primary-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Chargement...
                  </>
                ) : (
                  <>
                    <Search className="w-5 h-5" />
                    Rechercher
                  </>
                )}
              </button>
              <p className="text-xs text-gray-500 text-center">Exemple: 189 742 216 00 022</p>
            </div>
          )}
        </div>

        {/* Étape 2 */}
        {step >= 1 && (
          <div className="border-2 border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step >= 2 ? 'bg-green-500' : 'bg-gray-300'
              }`}>
                {step >= 2 ? <CheckCircle className="w-5 h-5 text-white" /> : <span className="text-white font-bold">2</span>}
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">Filtrer les doublons</h3>
                <p className="text-sm text-gray-600">Garder les formations uniques</p>
              </div>
              {step >= 2 && (
                <span className="text-sm font-medium text-green-600">{uniqueFormations.length} uniques</span>
              )}
            </div>

            {step === 1 && (
              <button
                onClick={handleFilterUnique}
                disabled={loading}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Filter className="w-5 h-5" />
                Filtrer les doublons
              </button>
            )}
          </div>
        )}

        {/* Étape 3 */}
        {step >= 2 && (
          <div className="border-2 border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step >= 3 ? 'bg-green-500' : 'bg-gray-300'
              }`}>
                {step >= 3 ? <CheckCircle className="w-5 h-5 text-white" /> : <span className="text-white font-bold">3</span>}
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">Rechercher les codes RNCP</h3>
                <p className="text-sm text-gray-600">Un par un avec délai de 5s</p>
              </div>
              {step >= 3 && (
                <span className="text-sm font-medium text-green-600">
                  {rncpFormations.filter(f => f.has_rncp).length} RNCP trouvés
                </span>
              )}
            </div>

            {step === 2 && (
              <button
                onClick={handleSearchRNCP}
                disabled={loading}
                className="w-full bg-purple-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Database className="w-5 h-5" />
                Rechercher RNCP
              </button>
            )}
          </div>
        )}

        {/* Étape 4 */}
        {step >= 3 && (
          <div className="border-2 border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step >= 4 ? 'bg-green-500' : 'bg-gray-300'
              }`}>
                {step >= 4 ? <CheckCircle className="w-5 h-5 text-white" /> : <span className="text-white font-bold">4</span>}
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">Enrichir avec compétences</h3>
                <p className="text-sm text-gray-600">Récupérer blocs et compétences</p>
              </div>
              {step >= 4 && (
                <span className="text-sm font-medium text-green-600">
                  {enrichedFormations.filter(f => f.is_enriched).length} enrichies
                </span>
              )}
            </div>

            {step === 3 && (
              <button
                onClick={handleEnrichCompetences}
                disabled={loading || rncpFormations.filter(f => f.has_rncp).length === 0}
                className="w-full bg-indigo-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Award className="w-5 h-5" />
                Enrichir les compétences
              </button>
            )}
          </div>
        )}

        {/* Étape 5 */}
        {step >= 4 && (
          <div className="border-2 border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step >= 5 ? 'bg-green-500' : 'bg-gray-300'
              }`}>
                {step >= 5 ? <CheckCircle className="w-5 h-5 text-white" /> : <span className="text-white font-bold">5</span>}
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">Trouver les paires</h3>
                <p className="text-sm text-gray-600">Détecter les formations similaires</p>
              </div>
              {step >= 5 && (
                <span className="text-sm font-medium text-green-600">{pairs.length} paire(s)</span>
              )}
            </div>

            {step === 4 && (
              <button
                onClick={handleFindPairs}
                disabled={loading || enrichedFormations.filter(f => f.is_enriched).length === 0}
                className="w-full bg-pink-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-pink-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <GitMerge className="w-5 h-5" />
                Trouver les paires
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  // Rendu de la progression
  const renderProgress = () => {
    if (!loading || currentProgress.total === 0) return null;

    return (
      <div className="fixed bottom-8 right-8 bg-white rounded-lg shadow-xl p-4 border-2 border-primary-200 max-w-md z-50">
        <div className="flex items-center gap-3 mb-2">
          <Loader2 className="w-5 h-5 text-primary-600 animate-spin" />
          <div className="flex-1">
            <div className="font-medium text-gray-900">
              {currentProgress.current} / {currentProgress.total}
            </div>
            <div className="text-sm text-gray-600">{currentProgress.message}</div>
          </div>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-primary-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${(currentProgress.current / currentProgress.total) * 100}%` }}
          />
        </div>
      </div>
    );
  };

  // Rendu des résultats
  const renderResults = () => {
    if (step < 5) return null;

    return (
      <div className="mt-8 space-y-6">
        <h2 className="text-2xl font-bold text-gray-900">Résultats</h2>

        {pairs.length === 0 ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
            <AlertCircle className="w-12 h-12 text-yellow-600 mx-auto mb-3" />
            <p className="text-gray-700">Aucune paire de formations détectée</p>
          </div>
        ) : (
          <div className="space-y-6">
            {pairs.map((pair, idx) => (
              <div key={idx} className="bg-white border-2 border-primary-200 rounded-lg overflow-hidden">
                {/* En-tête */}
                <div className="bg-gradient-to-r from-primary-600 to-purple-600 text-white p-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-bold">Paire #{idx + 1}</h3>
                    <div className="bg-white/20 rounded-full px-4 py-2">
                      <span className="text-2xl font-bold">{pair.matchPercentage}%</span>
                    </div>
                  </div>
                </div>

                {/* Comparaison */}
                <div className="grid grid-cols-2 divide-x divide-gray-200">
                  <div className="p-4">
                    <h4 className="font-bold text-gray-900 mb-2">{pair.formation1.title}</h4>
                    <p className="text-sm text-primary-600 font-semibold mb-2">
                      {pair.formation1.rncp_code.toUpperCase().startsWith('RNCP')
                        ? pair.formation1.rncp_code.toUpperCase()
                        : `RNCP${pair.formation1.rncp_code}`}
                    </p>
                    <div className="flex items-center gap-3 text-sm text-gray-600 mb-3">
                      <div className="bg-blue-100 text-blue-800 px-2 py-1 rounded">
                        {pair.formation1.nb_blocs || pair.formation1.blocs_competences?.length || 0} blocs
                      </div>
                      <div className="bg-green-100 text-green-800 px-2 py-1 rounded">
                        {pair.formation1.competences.length} compétences
                      </div>
                    </div>

                    {/* Affichage des blocs */}
                    {pair.formation1.blocs_competences && pair.formation1.blocs_competences.length > 0 && (
                      <div className="mt-3 space-y-2">
                        <p className="text-xs font-semibold text-gray-700 uppercase">Blocs de compétences:</p>
                        {pair.formation1.blocs_competences.slice(0, 3).map((bloc, bIdx) => (
                          <div key={bIdx} className="bg-gray-50 rounded p-2 text-xs">
                            <div className="font-medium text-gray-800">{bloc.intitule || bloc.title}</div>
                            {bloc.nb_competences && (
                              <div className="text-gray-500 mt-1">{bloc.nb_competences} compétences</div>
                            )}
                          </div>
                        ))}
                        {pair.formation1.blocs_competences.length > 3 && (
                          <div className="text-xs text-gray-500 italic">
                            ... et {pair.formation1.blocs_competences.length - 3} autre(s) bloc(s)
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="p-4">
                    <h4 className="font-bold text-gray-900 mb-2">{pair.formation2.title}</h4>
                    <p className="text-sm text-primary-600 font-semibold mb-2">
                      {pair.formation2.rncp_code.toUpperCase().startsWith('RNCP')
                        ? pair.formation2.rncp_code.toUpperCase()
                        : `RNCP${pair.formation2.rncp_code}`}
                    </p>
                    <div className="flex items-center gap-3 text-sm text-gray-600 mb-3">
                      <div className="bg-blue-100 text-blue-800 px-2 py-1 rounded">
                        {pair.formation2.nb_blocs || pair.formation2.blocs_competences?.length || 0} blocs
                      </div>
                      <div className="bg-green-100 text-green-800 px-2 py-1 rounded">
                        {pair.formation2.competences.length} compétences
                      </div>
                    </div>

                    {/* Affichage des blocs */}
                    {pair.formation2.blocs_competences && pair.formation2.blocs_competences.length > 0 && (
                      <div className="mt-3 space-y-2">
                        <p className="text-xs font-semibold text-gray-700 uppercase">Blocs de compétences:</p>
                        {pair.formation2.blocs_competences.slice(0, 3).map((bloc, bIdx) => (
                          <div key={bIdx} className="bg-gray-50 rounded p-2 text-xs">
                            <div className="font-medium text-gray-800">{bloc.intitule || bloc.title}</div>
                            {bloc.nb_competences && (
                              <div className="text-gray-500 mt-1">{bloc.nb_competences} compétences</div>
                            )}
                          </div>
                        ))}
                        {pair.formation2.blocs_competences.length > 3 && (
                          <div className="text-xs text-gray-500 italic">
                            ... et {pair.formation2.blocs_competences.length - 3} autre(s) bloc(s)
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Compétences communes */}
                <div className="bg-green-50 p-4 border-t border-gray-200">
                  <div className="font-medium text-gray-900 mb-2">
                    {pair.commonCompetences.length} compétence(s) commune(s)
                  </div>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {pair.commonCompetences.slice(0, 5).map((cc, i) => (
                      <div key={i} className="text-sm text-gray-700 bg-white rounded px-2 py-1">
                        {cc.competence1.substring(0, 100)}...
                      </div>
                    ))}
                    {pair.commonCompetences.length > 5 && (
                      <div className="text-sm text-gray-500 italic">
                        ... et {pair.commonCompetences.length - 5} autre(s)
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto p-8">
        {/* En-tête */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Building2 className="w-8 h-8 text-primary-600" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Test Onboarding</h1>
              <p className="text-gray-600">Analyse étape par étape des formations</p>
            </div>
          </div>
        </div>

        {/* Étapes */}
        <div className="bg-white rounded-lg shadow-md p-6">
          {renderStepButtons()}
        </div>

        {/* Résultats */}
        {renderResults()}

        {/* Progression */}
        {renderProgress()}
      </div>
    </div>
  );
};

export default OnboardingTest;
